import { Loader } from '@myosh/odin-components';
import type React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { useGetThemeQuery } from '@/api/generated/restV4-api';

type Theme = Record<string, string>;

interface ThemeContextProps {
  children: React.ReactNode;
}

const ThemeContext = createContext<Theme | undefined>(undefined);

export const ThemeProvider: React.FC<ThemeContextProps> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>();
  const [loaded, setLoaded] = useState(false);

  const { data, isLoading, isFetching } = useGetThemeQuery();

  useEffect(() => {
    if (!isLoading && !isFetching) {
      if (data) {
        const keys = Object.keys(data);
        for (let i = 0, length = keys.length; i < length; i++) {
          const color = data[keys[i]];
          if (CSS.supports('color', color)) {
            document.documentElement.style.setProperty(`--${keys[i]}`, color);
          } else {
            console.warn(`The CSS color '${color}' is not supported.`);
          }
        }

        setTheme(data);
        setLoaded(true);
      } else {
        // unable to fetch theme, continue with default
        console.warn('Unable to load user theme, continuing with default app theme.');
        setLoaded(true);
      }
    }
  }, [data, isLoading, isFetching]);

  return (
    <ThemeContext.Provider value={theme}>
      {loaded ? (
        children
      ) : (
        <div className="mx-auto flex h-screen w-full flex-col items-center justify-center">
          <Loader title="Loading Theme" />
        </div>
      )}
    </ThemeContext.Provider>
  );
};

// context consumer hook
export const useThemeContext = () => {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error('useThemeContext must be used within ThemeProvider');
  }

  return context;
};
